import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTable, useSortBy, useGlobalFilter, useFilters } from 'react-table';
import { HelmetProvider } from 'react-helmet-async';

// Page
import '../App.css';
import Header from './Header';
import PageMetadata from './PageMetadata';
import Footer from './Footer';
import BackToTop from './BackToTop';

// Tracker
import './Tracker.css';
import TrackerColumns from './TrackerColumns';
import RenderTrackerDesktop from './RenderTrackerDesktop';
import RenderTrackerMobile from './RenderTrackerMobile';

// Admin
import { useAdmin } from '../contexts/AdminContext';
import { useOrgs } from '../hooks/useLibraryData';
import EditableText from './admin/EditableText';
import ClaimEntryForm from './admin/ClaimEntryForm';

// Short URL aliases that don't match D1 slugs directly
const SHORT_URL_MAP = {
    'church': 'israel-denies-church-killing',
    'captives': 'palestinian-captives-stripped',
    'elderly': 'israeli-soldier-helps',
    // Legacy aliases that map to the same slugs
    'captives-stripped': 'palestinian-captives-stripped',
    'israel-denies-church-killing': 'israel-denies-church-killing',
    'israel-helps-elderly-man': 'israeli-soldier-helps',
    'israeli-state-denies': 'israel-denies-church-killing',
    'israeli-soldier-helps': 'israeli-soldier-helps',
    'palestinian-captives-stripped': 'palestinian-captives-stripped',
    // /claim was the old generic route — pointed to UNRWA topic
    'claim': 'unrwa-is-infiltrated-by-hamas',
};

// Maps D1 entry_type to the claimText/claimTag pair used by TrackerColumns
function mapEntryType(entryType) {
    switch (entryType) {
        case 'context':
            return { claimText: 'Context', claimTag: 'tag-context' };
        case 'claim':
            return { claimText: 'The claim', claimTag: 'tag-claim' };
        case 'debunk':
            return { claimText: 'The debunk', claimTag: 'tag-debunk' };
        default:
            return { claimText: entryType, claimTag: 'tag-context' };
    }
}

// Transforms API entries into the flat row format TrackerColumns expects
// Adds _entryId and _entryIndex for admin operations
function transformEntries(topic, entries) {
    return entries.map((entry, index) => {
        const sources = typeof entry.sources === 'string'
            ? JSON.parse(entry.sources)
            : (entry.sources || []);

        const { claimText, claimTag } = mapEntryType(entry.entry_type);

        return {
            claimTitle: topic.title,
            date: entry.date || '',
            claim: { claimText, claimTag },
            description: {
                summary: entry.summary || '',
                details: entry.details || '',
            },
            sources: sources,
            _entryId: entry.id,
            _entryIndex: index,
        };
    });
}

export default function ClaimPage() {
    const location = useLocation();
    const { isAdmin } = useAdmin();
    const { orgs: allOrgs } = useOrgs();

    // Extract slug from URL path
    const slug = location.pathname.replace(/^\//, '');
    const resolvedSlug = SHORT_URL_MAP[slug] || slug;

    const [topic, setTopic] = useState(null);
    const [rawEntries, setRawEntries] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Admin state
    const [editingEntry, setEditingEntry] = useState(null); // entry object for edit modal
    const [showNewEntryForm, setShowNewEntryForm] = useState(false);
    const [showOrgPicker, setShowOrgPicker] = useState(false);
    const [orgSearch, setOrgSearch] = useState('');

    // Fetch topic + entries from D1 API
    const fetchClaim = useCallback(() => {
        setLoading(true);
        setError(null);

        fetch(`/api/claims/${resolvedSlug}`)
            .then(res => {
                if (!res.ok) throw new Error(`Claim not found (${res.status})`);
                return res.json();
            })
            .then(data => {
                const topicData = data.topic || data;
                const entries = data.entries || [];
                setTopic(topicData);
                setRawEntries(entries);
                setTableData(transformEntries(topicData, entries));
                setLoading(false);
            })
            .catch(err => {
                console.error('Failed to load claim:', err);
                setError(err.message);
                setLoading(false);
            });
    }, [resolvedSlug]);

    useEffect(() => { fetchClaim(); }, [fetchClaim]);

    // Mobile detection
    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 576);
    const [forceRender, setForceRender] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            const newIsMobileView = window.innerWidth <= 576;
            setIsMobileView(newIsMobileView);
            if (newIsMobileView) {
                setForceRender(prev => !prev);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // --- Admin API helpers ---

    const saveTopicField = async (field, value) => {
        try {
            const response = await fetch(`/api/claims/${topic.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ [field]: value }),
            });
            if (!response.ok) {
                const err = await response.json();
                alert('Save failed: ' + (err.error || 'Unknown error'));
                return false;
            }
            // Update local state
            setTopic(prev => ({ ...prev, [field]: value }));
            return true;
        } catch (error) {
            alert('Save failed: network error');
            return false;
        }
    };

    const handleSaveEntry = async (entryData) => {
        const isEdit = !!entryData.id;
        const url = isEdit
            ? `/api/claims/entries/${entryData.id}`
            : '/api/claims/entries';
        const method = isEdit ? 'PUT' : 'POST';

        const response = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(entryData),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to save entry');
        }
        // Refetch claim data after mutation
        fetchClaim();
    };

    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm('Delete this entry? This cannot be undone.')) return;

        try {
            const response = await fetch(`/api/claims/entries/${entryId}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!response.ok) {
                const err = await response.json();
                alert('Delete failed: ' + (err.error || 'Unknown error'));
                return;
            }
            fetchClaim();
        } catch (error) {
            alert('Delete failed: network error');
        }
    };

    const toggleRelatedOrg = async (orgId) => {
        const current = topic.relatedOrgs || [];
        const updated = current.includes(orgId)
            ? current.filter(id => id !== orgId)
            : [...current, orgId];
        await saveTopicField('relatedOrgs', updated);
    };

    // Claim title list for dropdown filter
    const uniqueClaimTitles = useMemo(() => {
        const titles = new Set(tableData.map(item => item.claimTitle));
        return Array.from(titles);
    }, [tableData]);

    // Table columns
    const columns = useMemo(() => TrackerColumns({ isMobileView }), [isMobileView]);

    // react-table instance
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        setGlobalFilter,
        state,
    } = useTable(
        {
            columns,
            data: tableData,
            initialState: {
                sortBy: [{ id: 'date', desc: false }],
            },
            disableSortRemove: true,
        },
        useFilters,
        useGlobalFilter,
        useSortBy
    );

    const { globalFilter } = state;

    // Loading state
    if (loading) {
        return (
            <HelmetProvider>
                <>
                    <span className="header-container-fixed">
                        <Header />
                    </span>
                    <div className="tracker-container" style={{ padding: '80px 40px', textAlign: 'center' }}>
                        <p>Loading claim data...</p>
                    </div>
                    <Footer />
                </>
            </HelmetProvider>
        );
    }

    // Error state
    if (error) {
        return (
            <HelmetProvider>
                <>
                    <span className="header-container-fixed">
                        <Header />
                    </span>
                    <div className="tracker-container" style={{ padding: '80px 40px', textAlign: 'center' }}>
                        <p>Could not load this claim. Please try again later.</p>
                    </div>
                    <Footer />
                </>
            </HelmetProvider>
        );
    }

    // Resolve related orgs to full objects for display
    const relatedOrgObjects = (topic.relatedOrgs || [])
        .map(orgId => allOrgs.find(o => o.id === orgId))
        .filter(Boolean);

    const filteredOrgs = orgSearch.trim()
        ? allOrgs.filter(o => o.name.toLowerCase().includes(orgSearch.toLowerCase()))
        : allOrgs.slice(0, 20);

    // Page metadata
    const metadataProps = {
        url: `https://hasbaratracker.com/${topic.custom_url || slug}`,
        title: topic.title,
        description: topic.summary,
        twitterTitle: topic.title,
        twitterDescription: topic.summary,
        twitterUrl: `https://hasbaratracker.com/${topic.custom_url || slug}`,
    };

    return (
        <HelmetProvider>
            <>
                <PageMetadata {...metadataProps} />

                {/* Header (fixed) */}
                <span className="header-container-fixed">
                    <Header />

                    {/* Claim summary — with admin inline editing */}
                    <div className="claim-summary container mt-10 mobile:w-[100%] mobile:mt-[5%]">
                        <div className="mobile:text-xs laptop:w-7/12 laptop:text-md font-lores font-bold">
                            The claim</div>

                        <div className="mobile:mt-0 mobile:text-xs laptop:text-md laptop:w-7/12 mb-2 mt-1">
                            '{isAdmin ? (
                                <EditableText
                                    value={topic.title}
                                    onSave={(val) => saveTopicField('title', val)}
                                    tag="span"
                                />
                            ) : topic.title}'
                        </div>

                        <div className="mobile:text-xs mobile:mb-5 laptop:w-[60%] display:flex text-sm text-grey-faded mt-2 leading-6">
                            {isAdmin ? (
                                <EditableText
                                    value={topic.summary}
                                    onSave={(val) => saveTopicField('summary', val)}
                                    tag="span"
                                    multiline={true}
                                />
                            ) : topic.summary}
                        </div>

                        {/* Related organisations */}
                        {(relatedOrgObjects.length > 0 || isAdmin) && (
                            <div style={{ marginTop: '12px' }}>
                                {relatedOrgObjects.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                                        {relatedOrgObjects.map(org => (
                                            <Link
                                                key={org.id}
                                                to={`/organisations/${org.slug}`}
                                                style={{
                                                    fontSize: '0.75rem',
                                                    padding: '2px 10px',
                                                    background: '#222',
                                                    border: '1px solid #444',
                                                    borderRadius: '12px',
                                                    color: '#ccc',
                                                    textDecoration: 'none',
                                                }}
                                            >
                                                {org.name}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                                {isAdmin && (
                                    <button
                                        className="admin-btn-add"
                                        onClick={() => setShowOrgPicker(!showOrgPicker)}
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        {showOrgPicker ? 'Done' : '+ Link org'}
                                    </button>
                                )}
                                {isAdmin && showOrgPicker && (
                                    <div style={{ marginTop: '8px', maxWidth: '400px' }}>
                                        <input
                                            className="admin-edit-input"
                                            value={orgSearch}
                                            onChange={e => setOrgSearch(e.target.value)}
                                            placeholder="Search organisations..."
                                            autoFocus
                                        />
                                        <div className="admin-org-picker" style={{ marginTop: '4px' }}>
                                            {filteredOrgs.map(o => (
                                                <button
                                                    key={o.id}
                                                    className={`admin-tag-option ${(topic.relatedOrgs || []).includes(o.id) ? 'active' : ''}`}
                                                    onClick={() => toggleRelatedOrg(o.id)}
                                                >
                                                    {o.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Admin: published toggle */}
                        {isAdmin && (
                            <div style={{ marginTop: '8px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <label style={{ fontSize: '0.75rem', color: '#888', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={topic.published}
                                        onChange={e => saveTopicField('published', e.target.checked ? 1 : 0)}
                                    />
                                    Published
                                </label>
                                <span style={{ fontSize: '0.7rem', color: '#555' }}>
                                    ID: {topic.id}
                                </span>
                            </div>
                        )}
                    </div>
                </span>

                {/* Admin: per-entry edit/delete controls */}
                {isAdmin && rawEntries.length > 0 && (
                    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
                        <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{rawEntries.length} entries ({rawEntries.filter(e => e.is_draft).length} drafts)</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}>
                            {rawEntries.map((entry, idx) => (
                                <div key={entry.id} style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '6px 10px', background: '#111', borderRadius: '4px',
                                    border: entry.is_draft ? '1px dashed #555' : '1px solid #222',
                                    fontSize: '0.8rem',
                                }}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                        <span style={{
                                            padding: '1px 6px', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 'bold',
                                            background: entry.entry_type === 'claim' ? '#8B0000' : entry.entry_type === 'debunk' ? '#006400' : '#333',
                                            color: '#fff',
                                        }}>
                                            {entry.entry_type}
                                        </span>
                                        <span style={{ color: '#888', flexShrink: 0 }}>{entry.date || 'No date'}</span>
                                        <span style={{ color: '#ccc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {entry.summary?.substring(0, 80) || '(no summary)'}
                                        </span>
                                        {entry.is_draft && <span style={{ color: '#888', fontSize: '0.7rem' }}>[DRAFT]</span>}
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                                        <button
                                            className="admin-btn-small"
                                            onClick={() => setEditingEntry(entry)}
                                            style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="admin-btn-remove"
                                            onClick={() => handleDeleteEntry(entry.id)}
                                            style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            className="admin-btn-add"
                            onClick={() => setShowNewEntryForm(true)}
                        >
                            + Add entry
                        </button>
                    </div>
                )}

                {/* Desktop view */}
                {!isMobileView && (
                    <RenderTrackerDesktop
                        {...{
                            headerGroups,
                            getTableProps,
                            getTableBodyProps,
                            rows,
                            prepareRow,
                            globalFilter,
                            setGlobalFilter,
                            uniqueClaimTitles,
                        }}
                    />
                )}

                {/* Mobile view */}
                {isMobileView && (
                    <RenderTrackerMobile
                        {...{
                            headerGroups,
                            getTableProps,
                            getTableBodyProps,
                            rows,
                            prepareRow,
                            globalFilter,
                            setGlobalFilter,
                            uniqueClaimTitles,
                        }}
                    />
                )}

                {/* Admin: add entry button below the table too */}
                {isAdmin && (
                    <div style={{ maxWidth: '1200px', margin: '16px auto', padding: '0 20px' }}>
                        <button
                            className="admin-btn-add"
                            onClick={() => setShowNewEntryForm(true)}
                        >
                            + Add entry
                        </button>
                    </div>
                )}

                <BackToTop />
                <Footer />

                {/* Entry edit modal */}
                {editingEntry && (
                    <ClaimEntryForm
                        entry={editingEntry}
                        topicId={topic.id}
                        onSave={handleSaveEntry}
                        onClose={() => setEditingEntry(null)}
                    />
                )}

                {/* New entry modal */}
                {showNewEntryForm && (
                    <ClaimEntryForm
                        topicId={topic.id}
                        onSave={handleSaveEntry}
                        onClose={() => setShowNewEntryForm(false)}
                    />
                )}
            </>
        </HelmetProvider>
    );
}
